import { supabase } from '../config/supabase';
import QRCode from 'qrcode';

export interface VerificationToken {
    id: string;
    ticketId: string;
    token: string;
    pin: string; // Only available when generating
    createdAt: Date;
    expiresAt: Date;
    createdBy: string;
    scannedAt?: Date;
    scannedBy?: string;
    isUsed: boolean;
    isExpired: boolean;
}

/**
 * Service for QR-based ticket verification with PIN
 */
export class VerificationService {
    private static readonly TOKEN_VALIDITY_HOURS = 24;
    // Use environment variable for production, fallback to window.location.origin for dev
    private static readonly BASE_URL = import.meta.env.VITE_APP_URL || window.location.origin;

    /**
     * Generate a new verification token with QR code and PIN
     */
    static async generateVerificationToken(
        ticketId: string,
        userId: string
    ): Promise<{ token: VerificationToken; qrCodeDataUrl: string } | null> {
        try {
            // Generate random PIN (4 digits)
            const pin = this.generatePIN();

            // Generate unique token
            const token = this.generateToken();

            // Hash PIN for storage (simple hash - in production use bcrypt)
            const pinHash = await this.hashPIN(pin);

            // Calculate expiration
            const expiresAt = new Date();
            expiresAt.setHours(expiresAt.getHours() + this.TOKEN_VALIDITY_HOURS);

            // Insert to database
            const { data, error } = await supabase
                .from('verification_tokens')
                .insert({
                    ticket_id: ticketId,
                    token: token,
                    pin_hash: pinHash,
                    created_by: userId,
                    expires_at: expiresAt.toISOString(),
                    is_used: false,
                    is_expired: false
                })
                .select()
                .single();

            if (error) throw error;

            // Generate QR code URL
            const verificationUrl = `${this.BASE_URL}/verify/${token}`;
            const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
                width: 300,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                }
            });

            const tokenData: VerificationToken = {
                id: data.id,
                ticketId: data.ticket_id,
                token: data.token,
                pin: pin, // Return PIN to show to user
                createdAt: new Date(data.created_at),
                expiresAt: new Date(data.expires_at),
                createdBy: data.created_by,
                scannedAt: data.scanned_at ? new Date(data.scanned_at) : undefined,
                scannedBy: data.scanned_by,
                isUsed: data.is_used,
                isExpired: data.is_expired
            };

            return { token: tokenData, qrCodeDataUrl };
        } catch (error) {
            console.error('Error generating verification token:', error);
            return null;
        }
    }

    /**
     * Verify token and PIN, then close ticket with signature
     * Only the assigned technician can close the ticket
     */
    static async verifyAndCloseTicket(
        token: string,
        pin: string,
        technicianId: string,
        technicianName: string,
        signatureDataUrl?: string
    ): Promise<{ success: boolean; message: string; ticketId?: string }> {
        try {
            // Get token from database with ticket info
            const { data: tokenData, error: fetchError } = await supabase
                .from('verification_tokens')
                .select(`
                    *,
                    tickets (
                        id,
                        ticket_number,
                        assigned_to,
                        status
                    )
                `)
                .eq('token', token)
                .single();

            if (fetchError || !tokenData) {
                return { success: false, message: 'Código QR inválido' };
            }

            // Check if already used
            if (tokenData.is_used) {
                return { success: false, message: 'Este código ya fue utilizado' };
            }

            // Check if expired
            if (new Date(tokenData.expires_at) < new Date()) {
                await supabase
                    .from('verification_tokens')
                    .update({ is_expired: true })
                    .eq('id', tokenData.id);
                return { success: false, message: 'Este código ha expirado' };
            }

            // IMPORTANT: Verify that the technician is assigned to this ticket
            const ticket = tokenData.tickets;
            if (ticket.assigned_to !== technicianId) {
                return {
                    success: false,
                    message: 'Solo el técnico asignado a este ticket puede cerrarlo'
                };
            }

            // Verify PIN
            const pinValid = await this.verifyPIN(pin, tokenData.pin_hash);
            if (!pinValid) {
                return { success: false, message: 'PIN incorrecto' };
            }

            // Upload signature if provided
            let signatureUrl = null;
            if (signatureDataUrl) {
                signatureUrl = await this.uploadSignature(tokenData.ticket_id, signatureDataUrl);
            }

            // Mark token as used
            await supabase
                .from('verification_tokens')
                .update({
                    is_used: true,
                    scanned_at: new Date().toISOString(),
                    scanned_by: technicianId
                })
                .eq('id', tokenData.id);

            // Close the ticket
            const { error: updateError } = await supabase
                .from('tickets')
                .update({
                    status: 'CLOSED', // Change to CLOSED directly as requested
                    updated_at: new Date().toISOString()
                })
                .eq('id', tokenData.ticket_id);

            if (updateError) throw updateError;

            // Add timeline event with technician name
            await supabase
                .from('timeline_events')
                .insert({
                    ticket_id: tokenData.ticket_id,
                    user_id: technicianId,
                    user_name: technicianName,
                    event_type: 'STATUS_CHANGE',
                    content: `Ticket cerrado mediante verificación QR + PIN + Firma por ${technicianName}`,
                    metadata: signatureUrl ? { signatureUrl } : undefined
                });

            return {
                success: true,
                message: 'Ticket cerrado exitosamente con firma',
                ticketId: tokenData.ticket_id
            };
        } catch (error) {
            console.error('Error verifying token:', error);
            return { success: false, message: 'Error al verificar el código' };
        }
    }

    /**
     * Upload signature image to storage
     */
    private static async uploadSignature(ticketId: string, dataUrl: string): Promise<string | null> {
        try {
            // Convert data URL to Blob
            const res = await fetch(dataUrl);
            const blob = await res.blob();

            const fileName = `signatures/${ticketId}_${Date.now()}.png`;

            // Upload to 'attachments' bucket (assuming it exists and public)
            const { error } = await supabase.storage
                .from('attachments')
                .upload(fileName, blob, {
                    contentType: 'image/png',
                    upsert: true
                });

            if (error) {
                console.error('Error uploading signature:', error);
                return null;
            }

            const { data } = supabase.storage
                .from('attachments')
                .getPublicUrl(fileName);

            return data.publicUrl;
        } catch (error) {
            console.error('Error handling signature upload:', error);
            return null;
        }
    }

    /**
     * Get token info (without PIN)
     */
    static async getTokenInfo(token: string): Promise<VerificationToken | null> {
        try {
            const { data, error } = await supabase
                .from('verification_tokens')
                .select(`
                    *,
                    tickets (
                        ticket_number,
                        site_name,
                        category
                    )
                `)
                .eq('token', token)
                .single();

            if (error || !data) return null;

            return {
                id: data.id,
                ticketId: data.ticket_id,
                token: data.token,
                pin: '', // Never return PIN
                createdAt: new Date(data.created_at),
                expiresAt: new Date(data.expires_at),
                createdBy: data.created_by,
                scannedAt: data.scanned_at ? new Date(data.scanned_at) : undefined,
                scannedBy: data.scanned_by,
                isUsed: data.is_used,
                isExpired: data.is_expired || new Date(data.expires_at) < new Date()
            };
        } catch (error) {
            console.error('Error getting token info:', error);
            return null;
        }
    }

    /**
     * Generate random 4-digit PIN
     */
    private static generatePIN(): string {
        return Math.floor(1000 + Math.random() * 9000).toString();
    }

    /**
     * Generate unique token
     */
    private static generateToken(): string {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Hash PIN (simple version - use bcrypt in production)
     */
    private static async hashPIN(pin: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(pin);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }

    /**
     * Verify PIN against hash
     */
    private static async verifyPIN(pin: string, hash: string): Promise<boolean> {
        const pinHash = await this.hashPIN(pin);
        return pinHash === hash;
    }

    /**
     * Invalidate token
     */
    static async invalidateToken(tokenId: string): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('verification_tokens')
                .update({ is_expired: true })
                .eq('id', tokenId);

            return !error;
        } catch (error) {
            console.error('Error invalidating token:', error);
            return false;
        }
    }
}
